import { Component, inject, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EstadosDocumentoComponent } from '../../commons/estados-documento/estados-documento.component';
import { NoDataFoundComponent } from '../../commons/no-data-found/no-data-found.component';
import { AutofocusDirective } from '../../helpers/autofocus.directive';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MAT_DATE_LOCALE, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { CustomPaginator } from '../../commons/custom-paginator/custom-paginator';
import { DateService, MY_DATE_FORMATS } from '../../shared/date.service';
import { ScnackbarData, ToastService, SnackbarStyles } from '../../commons/toast.service';
import { Documento, ListadoDocumentoPaginado } from '../../models/Document';
import { FilterData } from '../borrador/borrador.component';
import { catchError, EMPTY, map, merge, startWith, switchMap } from 'rxjs';
import { AppTitleService } from '../../services/app-title.service';
import { MatDialog } from '@angular/material/dialog';
import { ReportService } from '../../services/report.service';
import { Router } from '@angular/router';
import { DocumentoService } from '../../services/documento.service';
import { DocumentTypeService } from '../../services/document-type.service';
import { SelectionModel } from '@angular/cdk/collections';
import { DocumentType } from '../../models/DocumentType';

@Component({
    selector: 'app-bandeja-archivados',
    imports: [
        MatTableModule, MatPaginatorModule, MatSortModule,
        MatSnackBarModule,
        MatToolbarModule, MatButtonModule, MatIconModule,
        MatInputModule, MatFormFieldModule,
        FormsModule, ReactiveFormsModule,
        MatSelectModule, AutofocusDirective,
        MatDatepickerModule, NoDataFoundComponent,
        MatCheckboxModule, MatProgressBarModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        EstadosDocumentoComponent
    ],
    providers: [
        { provide: MatPaginatorIntl, useClass: CustomPaginator },
        { provide: MAT_DATE_LOCALE, useValue: 'es-EC' },
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
        },
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    ],
    templateUrl: './bandeja-archivados.component.html',
    styleUrl: './bandeja-archivados.component.scss'
})
export class BandejaArchivadosComponent {
  displayedColumns: string[] = ['select', 'estados', 'asunto', 'fecha', 'numeroDocumento'];
  documentsPaginated: ListadoDocumentoPaginado | undefined
  dataSource = new MatTableDataSource<Documento>([]);
  selection = new SelectionModel<Documento>(true, []);

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  resultsLength = 0;
  isLoadingResults = true;

  documentTypes: DocumentType[] | undefined

  dateRangeInWeeks = 5
  initialDate = "2025-01-02"
  maxDate = new Date()

  snackbarData: ScnackbarData = {}

  filterForm = new FormGroup({
    fechaInicio: new FormControl(new Date(), {
      validators: [
        Validators.required
      ]
    }),
    fechaFin: new FormControl(new Date(), {
      validators: [
        Validators.required,
      ]
    }),
    searchText: new FormControl(""),
    documentType: new FormControl("")
  })

  constructor(
    private documentoSrv: DocumentoService,
    private documentTypeService: DocumentTypeService,
    private router: Router,
    private reportService: ReportService,
    public dialog: MatDialog,
    private toast: ToastService,
    private dateService: DateService
  ) {
    inject(AppTitleService).setTitle("Archivados");
  }

  ngOnInit(): void {
    const currentDate = new Date()
    let initialDate = new Date(this.initialDate)
    //initialDate.setDate(currentDate.getDate() - this.dateRangeInWeeks * 7)
    this.filterForm.controls.fechaFin.setValue(currentDate)
    this.filterForm.controls.fechaInicio.setValue(initialDate)
  }

  ngAfterViewInit(): void {

    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    this.documentTypeService.list().subscribe({
      next: (response: DocumentType[]) => {
        this.documentTypes = response;
        let documentTypeDescription = "Oficio"

        const defaultType = this.documentTypes.find(type => type.descripcion === documentTypeDescription);

        if (defaultType) {
          this.filterForm.controls.documentType.setValue("-1")

          merge(this.sort.sortChange, this.paginator.page).pipe(
            startWith({}),
            switchMap(() => {
              this.isLoadingResults = true;
              const filterData = this.createFilterData()
              return this.documentoSrv!.getListadoDocumentosArchivados(
                filterData.initialDate,
                filterData.endDate,
                filterData.searchText,
                filterData.documentType == -1 ? null : filterData.documentType,
                this.paginator.pageIndex + 1,
              ).pipe(catchError(() => EMPTY));
            }),
            map(data => {
              this.isLoadingResults = false;
              if (data === null) {
                return [];
              }
              this.resultsLength = data.totalDocumentos;
              return data;
            })
          ).subscribe({
            next: (response) => {
              this.documentsPaginated = response as ListadoDocumentoPaginado
              this.dataSource = new MatTableDataSource<Documento>(this.documentsPaginated.documentos)

              Object.keys(this.filterForm.controls).forEach(controlName => {
                if (controlName !== "searchText") {
                  this.filterForm.get(controlName)?.valueChanges.subscribe(_ => {
                    this.onControlValueChanged();
                  });
                }
              });
            }
          })
        }
      }
    });
  }

  onKeyUp(e: Event) {
    const codeKey: any = e;
    codeKey.which !== 13
      ? e.preventDefault()
      : this.onControlValueChanged();
  }

  onControlValueChanged(): void {
    this.dataSource = new MatTableDataSource<Documento>([])
    this.fectchAndInitializeDataSource()
  }

  reload(){
    this.dataSource = new MatTableDataSource<Documento>([])
    this.fectchAndInitializeDataSource();
  }

  fectchAndInitializeDataSource() {
    this.isLoadingResults = true;
    this.paginator.pageIndex = 0
    const filterData = this.createFilterData()
    this.documentoSrv!.getListadoDocumentosArchivados(
      filterData.initialDate,
      filterData.endDate,
      filterData.searchText,
      filterData.documentType == -1 ? null : filterData.documentType,
      this.paginator.pageIndex + 1,
    ).subscribe({
      next: (response) => {
        this.documentsPaginated = response as ListadoDocumentoPaginado
        this.dataSource = new MatTableDataSource<Documento>(this.documentsPaginated.documentos)
        this.resultsLength = this.documentsPaginated.totalDocumentos;
        this.isLoadingResults = false;
      }
    })
  }

  private createFilterData(): FilterData {
    const filterData: FilterData = {
      initialDate: this.dateService.formatDateToCustomString(this.filterForm.controls.fechaInicio.value!),
      endDate: this.dateService.formatDateToCustomString(this.filterForm.controls.fechaFin.value!),
      documentType: parseInt(this.filterForm.controls.documentType.value!),
      searchText: this.filterForm.controls.searchText.value!
    }
    return filterData
  }

  onRestore() {
    if (this.selection.selected.length === 0) {
      this.snackbarData.message = "Seleccione al menos una tarea"
      this.snackbarData.style = SnackbarStyles.Warning
      this.snackbarData.iconName = "warning"
      this.toast.show(this.snackbarData)
      return
    }
    const taskData = {
      documentIDs: this.selection.selected.map(document => document.idTarea),
    }
    this.router.navigateByUrl("documento/restaurar", { state: taskData })
  }

  onAddComment() {
    if (this.selection.selected.length === 0) {
      this.snackbarData.message = "Seleccione al menos una tarea"
      this.snackbarData.style = SnackbarStyles.Warning
      this.snackbarData.iconName = "warning"
      this.toast.show(this.snackbarData)
      return
    }
    const taskData = {
      documentIDs: this.selection.selected.map(document => document.idTarea),
    }
    this.router.navigateByUrl("comentar", { state: taskData })
  }

  clickedRow(row: Documento) {
    this.router.navigateByUrl("/resumen/" + row.idTarea)
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource!.data.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this.dataSource!.data);
  }

  checkboxLabel(row?: Documento): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id + 1}`;
  }

  formatDate(date: string): string {
    return this.dateService.formatCustomStringToDate(date)
  }

  onCallReport() {
    //https://127.0.0.1:8443/dfwreportes/rs/rpt/listadotipodocumento?P_CODIGO_TIPO_DOC=3
    var params = {
      P_CODIGO_TIPO_DOC: 3
    }
    var url: any = this.reportService.getUrl("listadotipodocumento", params);
    window.open(url, "_blank");
  }

}
